import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { ArrowUpTrayIcon, ArrowsRightLeftIcon, XMarkIcon, PresentationChartBarIcon, ExclamationTriangleIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { Block, BlockContent, DecompositionMode, Ensemble, ProjectTemplate, ProjectParameters, PrixReliability, PrixUnitaireSourceItem, isPrestation, SavedChiffrage } from '../types';
import { createVersion, VersionEvent } from '../types/Version';
import { ChiffragePhase, DEFAULT_PHASE_NAMES } from '../types/Timeline';
import { Consultation, ConsultationStatus } from '../types/Consultation';
import ConsultationPage from './ConsultationPage';
import { exportChiffrageToCSV } from '../utils/csvExport';
import { defaultBlocks } from '../data';
import ChiffHeader from './Chiff_header';
import { ProjectHeader, ProjectSummary } from './molecules';
import ChiffMenu from './Chiff_menu';
import { BlockEditor } from './ChiffWorkArea';
import ImportAnimationOverlay from './ImportAnimationOverlay';
import FeuilleDeVentePanel, { FeuilleDeVenteState, DEFAULT_FEUILLE_DE_VENTE } from './FeuilleDeVente/FeuilleDeVentePanel';
import VersionTabs from './VersionTabs';
import VersionComparisonPanel, { ComparisonSlot } from './VersionComparisonPanel';
import VersionEventMatrix from './VersionEventMatrix';
import LotTabs from './LotTabs';
import RecapSummary from './RecapSummary';
import { computeLotCurrentTotal, computeBudgetInfo, LotBudgetInfo } from '../utils/lotBudget';
import { BlockService } from '../services/BlockService';
import { useRatioPromotion } from '../../99-contexts/RatioPromotionContext';
import PhaseBar from './PhaseBar';
import { allData as ventilationAllData } from '../../1-bibliotheque-prix/data/ServicesData';
import { bestMatch } from '../../6-challenger-devis/utils/fuzzyMatch';
import { parsePrixString } from '../../6-challenger-devis/utils/priceParsing';
import { BlockCalculationService, DecompositionCalculationService } from '../services';
import { ChiffrageView } from '../types';

const LOADING_STEPS = [
  'Lecture du document PDF',
  'Identification des lots',
  'Extraction des unités de prix',
  'Vérification des données'
];

function sourcesToReliability(count: number): PrixReliability {
  if (count >= 5) return 'high';
  if (count >= 2) return 'medium';
  return 'low';
}

const MOCK_CHANTIERS = [
  { nom: 'Tour Haussmann', localisation: 'Paris 8e' },
  { nom: 'Résidence Léonard', localisation: 'Lyon 3e' },
  { nom: 'Campus Bordeaux Tech', localisation: 'Bordeaux' },
  { nom: 'Hôtel du Parc', localisation: 'Nice' },
  { nom: 'Centre Culturel Nord', localisation: 'Lille' },
  { nom: 'Immeuble Pasteur', localisation: 'Nantes' },
  { nom: 'Clinique Saint-Joseph', localisation: 'Toulouse' },
  { nom: 'ZAC les Bosquets', localisation: 'Montfermeil' },
];

function generateMockSources(basePriceStr: string | undefined, reliability: PrixReliability): PrixUnitaireSourceItem[] {
  const basePrice = parseFloat(basePriceStr ?? '0');
  if (!basePrice || basePrice <= 0) return [];
  const cfg = {
    high:   { count: [5, 8],  variance: 0.08, years: [2022, 2025] },
    medium: { count: [2, 4],  variance: 0.22, years: [2019, 2024] },
    low:    { count: [0, 1],  variance: 0.40, years: [2015, 2021] },
  }[reliability];
  const count = cfg.count[0] + Math.floor(Math.random() * (cfg.count[1] - cfg.count[0] + 1));
  return Array.from({ length: count }, (_, i) => {
    const variance = (Math.random() * 2 - 1) * cfg.variance;
    const prix = Math.round(basePrice * (1 + variance) * 100) / 100;
    const year = cfg.years[0] + Math.floor(Math.random() * (cfg.years[1] - cfg.years[0] + 1));
    const chantier = MOCK_CHANTIERS[i % MOCK_CHANTIERS.length];
    return { id: i + 1, nom: chantier.nom, localisation: chantier.localisation, annee: String(year), fournisseur: 'Base de données', prixUnitaire: prix, active: true };
  });
}

/** Applique une fiabilité aléatoire à chaque prestation, génère des sources mock cohérentes. */
function withRandomReliability(blocks: Block[]): Block[] {
  return blocks.map(b => {
    if (!isPrestation(b)) return b;
    const r = Math.random();
    const reliability: PrixReliability = r < 0.45 ? 'high' : r < 0.75 ? 'medium' : 'low';
    const prixUnitaireSources = generateMockSources(b.content.prixUnitaire, reliability);
    return { ...b, content: { ...b.content, prixUnitaireReliability: reliability, prixUnitaireSource: 'database' as const, prixUnitaireSources } };
  });
}

interface TimelineState {
  phases: ChiffragePhase[];
  activePhaseId: string;
  activeVersionId: string;
}

const DEFAULT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'remplacement-occultation',
    nom: 'Remplacement de l\'occultation',
    description: 'Volets, stores, rideaux',
    caracteristiques: { typologie: 'residentiel', annee: '2024', localisation: 'paris', projet: 'Occultation', fournisseur: 'Spécialiste occultation', categorie: 'menuiserie', prixMin: '50', prixMax: '500' },
    informationsProjet: 'Projet de remplacement des éléments d\'occultation.',
  },
  {
    id: 'renovation-electricite',
    nom: 'Rénovation électrique',
    description: 'Rénovation électrique complète',
    caracteristiques: { typologie: 'residentiel', annee: '2024', localisation: 'paris', projet: 'Électricité', fournisseur: 'Électricien certifié', categorie: 'electricite', prixMin: '100', prixMax: '1000' },
    informationsProjet: 'Rénovation complète de l\'installation électrique.',
  },
  {
    id: 'amenagement-bureau',
    nom: 'Aménagement de bureau',
    description: 'Aménagement d\'espaces professionnels',
    caracteristiques: { typologie: 'commercial', annee: '2024', localisation: 'paris', projet: 'Aménagement', fournisseur: 'Agencement professionnel', categorie: 'amenagement', prixMin: '200', prixMax: '2000' },
    informationsProjet: 'Aménagement complet d\'espaces de bureau.',
  },
];

// Conversion des blocs par défaut (Record<string, Block>) en tableau (Block[])
// Le BlockEditor attend tous les blocs (racines + enfants) en format tableau
function getDefaultBlocksArray(): Block[] {
  return Object.values(defaultBlocks);
}

const DEFAULT_SAVED_CHIFFRAGES: SavedChiffrage[] = [
  {
    id: 'saved-1',
    nom: 'Bureaux Haussmann — Réno',
    savedAt: new Date('2025-11-14'),
    blocks: withRandomReliability(getDefaultBlocksArray()),
    budgetTotal: '1850000',
    surfaceTraitee: '1200',
  },
  {
    id: 'saved-2',
    nom: 'Résidence Les Pins',
    savedAt: new Date('2025-12-03'),
    blocks: withRandomReliability(getDefaultBlocksArray()),
    budgetTotal: '920000',
    surfaceTraitee: '640',
  },
];

const ChiffrageContainer: React.FC = () => {
  // États du projet
  const [nomProjet, setNomProjet] = useState('Mon Super Projet');
  const [templates, setTemplates] = useState<ProjectTemplate[]>(DEFAULT_TEMPLATES);
  const [savedChiffrages, setSavedChiffrages] = useState<SavedChiffrage[]>(DEFAULT_SAVED_CHIFFRAGES);
  const [budgetTotal, setBudgetTotal] = useState('0');
  const [surfaceTraitee, setSurfaceTraitee] = useState('');
  const [informationsProjet, setInformationsProjet] = useState('');

  // Timeline : phases + version active dans un seul état
  const [timelineData, setTimelineData] = useState<TimelineState>(() => {
    const v1 = createVersion('V1', withRandomReliability(getDefaultBlocksArray()));
    const phases: ChiffragePhase[] = DEFAULT_PHASE_NAMES.map((name, i) => ({
      id: `phase-${i}`,
      name,
      order: i,
      versions: i === 0 ? [v1] : [],
    }));
    return { phases, activePhaseId: 'phase-0', activeVersionId: v1.id };
  });

  const activePhase = useMemo(
    () => timelineData.phases.find(p => p.id === timelineData.activePhaseId)!,
    [timelineData]
  );
  const versions = activePhase.versions;
  const { activeVersionId } = timelineData;

  const activeVersion = useMemo(
    () => activePhase.versions.find(v => v.id === activeVersionId),
    [activePhase, activeVersionId]
  );
  const activeEvents = useMemo(() => activeVersion?.events ?? [], [activeVersion]);

  // Comparaison inter-phases/versions
  const [comparisonSlots, setComparisonSlots] = useState<ComparisonSlot[]>([]);
  const [showComparisonPicker, setShowComparisonPicker] = useState(false);
  const isComparisonMode = comparisonSlots.length >= 2;
  const blocks: Block[] = useMemo(
    () => activePhase.versions.find(v => v.id === timelineData.activeVersionId)?.blocks ?? [],
    [activePhase, timelineData.activeVersionId]
  );

  const hasLots = useMemo(
    () => blocks.some(b => !isPrestation(b) && b.level === 0),
    [blocks]
  );

  const lots = useMemo(
    () => blocks.filter(b => !isPrestation(b) && b.level === 0),
    [blocks]
  );

  // Budget courant + initial + delta pour chaque lot — calcul centralisé partagé entre LotTabs et RecapSummary
  const lotBudgets = useMemo<Map<string, LotBudgetInfo>>(() => {
    const map = new Map<string, LotBudgetInfo>();
    for (const lot of lots) {
      const current = computeLotCurrentTotal(blocks, lot.id);
      map.set(lot.id, computeBudgetInfo(current, lot.budgetInitial));
    }
    return map;
  }, [blocks, lots]);

  const showRecapTab = lots.length > 1;

  // Onglet actif : null = Récap ; sinon l'id du lot visible
  const [activeLotId, setActiveLotId] = useState<string | null>(null);

  // Réinitialiser l'onglet actif si besoin :
  // - lot actif disparu → Récap (ou unique lot restant)
  // - aucun onglet actif alors qu'il n'y a pas de Récap (lot unique) → sélectionner ce lot
  useEffect(() => {
    if (activeLotId !== null && !lots.some(l => l.id === activeLotId)) {
      setActiveLotId(showRecapTab ? null : (lots[0]?.id ?? null));
      return;
    }
    if (activeLotId === null && !showRecapTab && lots.length === 1) {
      setActiveLotId(lots[0].id);
    }
  }, [activeLotId, lots, showRecapTab]);

  // IDs du lot actif + descendants (pour filtrer les blocs visibles)
  // On utilise parentId comme source de vérité (children[] peut être stale quand
  // on pousse de nouveaux blocs directement dans setBlocks sans mettre à jour le parent).
  const activeLotSubtreeIds = useMemo(() => {
    if (activeLotId === null) return null; // null => tous les blocs
    const childrenByParent = new Map<string, string[]>();
    for (const b of blocks) {
      if (b.parentId) {
        const arr = childrenByParent.get(b.parentId) ?? [];
        arr.push(b.id);
        childrenByParent.set(b.parentId, arr);
      }
    }
    const ids = new Set<string>([activeLotId]);
    let frontier: string[] = [activeLotId];
    while (frontier.length) {
      const next: string[] = [];
      for (const id of frontier) {
        const childIds = childrenByParent.get(id) ?? [];
        for (const childId of childIds) {
          if (!ids.has(childId)) {
            ids.add(childId);
            next.push(childId);
          }
        }
      }
      frontier = next;
    }
    return ids;
  }, [activeLotId, blocks]);

  const visibleBlocks = useMemo(() => {
    if (!activeLotSubtreeIds) return blocks;
    return blocks.filter(b => activeLotSubtreeIds.has(b.id));
  }, [blocks, activeLotSubtreeIds]);

  const pendingPointagesCount = useMemo(() => {
    let count = 0;
    for (const event of activeEvents) {
      const applicableLots = event.lotIds === null
        ? lots
        : lots.filter(l => event.lotIds!.includes(l.id));
      for (const lot of applicableLots) {
        if (!event.pointages[lot.id]) count++;
      }
    }
    return count;
  }, [activeEvents, lots]);

  // Mise à jour des blocs de la version active (accepte tableau ou fonction)
  const setBlocks = useCallback((newBlocksOrFn: Block[] | ((prev: Block[]) => Block[])) => {
    setTimelineData(prev => {
      const currentPhase = prev.phases.find(p => p.id === prev.activePhaseId);
      const current = currentPhase?.versions.find(v => v.id === prev.activeVersionId)?.blocks ?? [];
      const newBlocks = typeof newBlocksOrFn === 'function' ? newBlocksOrFn(current) : newBlocksOrFn;
      return {
        ...prev,
        phases: prev.phases.map(p =>
          p.id === prev.activePhaseId
            ? { ...p, versions: p.versions.map(v => v.id === prev.activeVersionId ? { ...v, blocks: newBlocks } : v) }
            : p
        ),
      };
    });
  }, []);
  
  // Paramètres du projet
  const [projectParameters, setProjectParameters] = useState<ProjectParameters>({
    TypologieProgramme: 'Tertiaire',
    ModaliteProgramme: 'Neuf',
    DevolutonMarche: 'Entreprise Générale',
    Gamme: 'Moyenne'
  });
  const [visibleParams, setVisibleParams] = useState<Set<keyof ProjectParameters>>(new Set());
  const [activeParams, setActiveParams] = useState<Set<keyof ProjectParameters>>(new Set());
  
  // États pour le menu
  const [selectedBlocks, setSelectedBlocks] = useState<Block[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Consultations
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [activeConsultationId, setActiveConsultationId] = useState<string | null>(null);

  const consultationMap = useMemo(() => {
    const map = new Map<string, { name: string; id: string; status: ConsultationStatus; retainedCompanyName?: string }>();
    for (const c of consultations) {
      const retainedCompanyName = c.retainedCompanyId
        ? c.companies.find(co => co.id === c.retainedCompanyId)?.name
        : undefined;
      for (const b of c.blocks) {
        if (!map.has(b.id)) {
          map.set(b.id, { name: c.name, id: c.id, status: c.status, retainedCompanyName });
        }
      }
    }
    return map;
  }, [consultations]);

  const handleConsulter = useCallback((selected: Block[]) => {
    const prestations = selected.filter(isPrestation);
    if (prestations.length === 0) return;

    // Include parent sections for context display in consultation table
    const parentIds = new Set(
      prestations.map(b => b.parentId).filter((id): id is string => !!id)
    );
    const parentBlocks = blocks.filter(b => parentIds.has(b.id));
    const blockIds = new Set([...parentBlocks.map(b => b.id), ...prestations.map(b => b.id)]);
    const consultationBlocks = blocks.filter(b => blockIds.has(b.id)); // preserves order

    // Auto-naming
    let name: string;
    if (parentIds.size === 1) {
      const parentId = Array.from(parentIds)[0];
      const parent = blocks.find(b => b.id === parentId);
      name = parent?.content.titre || `Consultation ${consultations.length + 1}`;
    } else {
      name = `Consultation mixte ${consultations.length + 1}`;
    }

    const newConsultation: Consultation = {
      id: `consultation-${Date.now()}`,
      name,
      blocks: consultationBlocks,
      companies: [],
      offers: [],
      status: 'draft',
      createdAt: new Date(),
    };
    setConsultations(prev => [...prev, newConsultation]);
    setActiveConsultationId(newConsultation.id);
  }, [blocks, consultations]);

  const handleUpdateConsultation = useCallback((updated: Consultation) => {
    setConsultations(prev => prev.map(c => c.id === updated.id ? updated : c));
  }, []);

  const handleOpenConsultation = useCallback((consultationId: string) => {
    setActiveConsultationId(consultationId);
  }, []);

  // Feuille de vente (état conservé ici — consommé par le panel vente et la décomposition F/MO)
  const [feuilleDeVente, setFeuilleDeVente] = useState<FeuilleDeVenteState>(DEFAULT_FEUILLE_DE_VENTE);

  // Vue globale du chiffrage. Défaut = 'detaillee' (vue de travail interne, tout visible) ;
  // 'ventilee' cache la décomposition F/MO ; 'client' cache aussi la ventilation (vue export).
  const [chiffrageView, setChiffrageView] = useState<ChiffrageView>('detaillee');

  // Paramètres de décomposition F/MO dérivés de la feuille de vente
  const decompositionParams = useMemo(
    () => ({
      tauxHoraire: feuilleDeVente.tauxHoraire,
      tempsApproDefault: feuilleDeVente.tempsApproDefault,
      coefDefaut: feuilleDeVente.coefDefaut,
      fraisChantierDefaut: feuilleDeVente.fraisChantierDefaut,
      fraisGenerauxDefaut: feuilleDeVente.fraisGenerauxDefaut,
      margeDefaut: feuilleDeVente.margeDefaut,
    }),
    [
      feuilleDeVente.tauxHoraire,
      feuilleDeVente.tempsApproDefault,
      feuilleDeVente.coefDefaut,
      feuilleDeVente.fraisChantierDefaut,
      feuilleDeVente.fraisGenerauxDefaut,
      feuilleDeVente.margeDefaut,
    ],
  );

  // Recalcule les prestations en mode détaillé quand les paramètres globaux changent.
  // Scope : version active uniquement. Évite la boucle grâce à la comparaison de référence.
  useEffect(() => {
    const recomputed = DecompositionCalculationService.recomputeBlocks(blocks, decompositionParams);
    if (recomputed !== blocks && recomputed.some((b, i) => b !== blocks[i])) {
      setBlocks(recomputed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decompositionParams]);

  const handleRetainCompany = useCallback((consultationId: string, companyId: string) => {
    const consultation = consultations.find(c => c.id === consultationId);
    if (!consultation) return;
    const offersForCompany = consultation.offers.filter(
      o => o.companyId === companyId && o.prixUnitaire !== null
    );
    setBlocks(prev => prev.map(block => {
      const offer = offersForCompany.find(o => o.blockId === block.id);
      if (!offer || offer.prixUnitaire === null) return block;
      const intQty = parseFloat(block.content.quantite ?? '1') || 1;
      const qty = offer.quantite !== null && offer.quantite !== undefined ? offer.quantite : intQty;
      const unite = offer.unite || block.content.unite;
      const pu = offer.prixUnitaire;
      const total = Math.round(pu * qty * 100) / 100;
      return {
        ...block,
        content: {
          ...block.content,
          prixUnitaire: String(pu),
          quantite: String(qty),
          unite: unite,
          prixTotal: String(total),
          prixTotalManual: false,
          prixUnitaireSource: 'user' as const,
          prixUnitaireReliability: undefined,
          prixUnitaireOriginal: block.content.prixUnitaire,
        },
      };
    }));
  }, [consultations, setBlocks]);

  const [showFeuilleDeVente, setShowFeuilleDeVente] = useState(false);

  // États pour l'import
  const [importPhase, setImportPhase] = useState<'idle' | 'loading' | 'animating'>('idle');
  const [loadingStep, setLoadingStep] = useState(0);
  const [importKey, setImportKey] = useState(0);
  const [reliabilityMap, setReliabilityMap] = useState<Record<string, PrixReliability>>({});

  // Refs pour les fonctions de sélection du BlockEditor
  const selectAllRef = useRef<(() => void) | null>(null);
  const clearSelectionRef = useRef<(() => void) | null>(null);

  // Gestion de l'import
  const handleImport = useCallback(() => {
    if (importPhase !== 'idle') return;

    // Pré-générer les niveaux de fiabilité (cohérence animation → éditeur)
    const map: Record<string, PrixReliability> = {};
    getDefaultBlocksArray().filter(b => isPrestation(b)).forEach(b => {
      const r = Math.random();
      map[b.id] = r < 0.45 ? 'high' : r < 0.75 ? 'medium' : 'low';
    });
    setReliabilityMap(map);

    setImportPhase('loading');
    setLoadingStep(0);

    // Progression des étapes du loader
    const timings = [1200, 2400, 3700, 4900];
    timings.forEach((delay, i) => {
      setTimeout(() => setLoadingStep(i + 1), delay);
    });

    // Passage à la phase d'animation après 5 secondes
    setTimeout(() => {
      setImportPhase('animating');
    }, 5000);
  }, [importPhase]);

  const handleImportComplete = useCallback(() => {
    setImportPhase('idle');
    setLoadingStep(0);
    setBlocks(withRandomReliability(getDefaultBlocksArray()));
    setImportKey(prev => prev + 1);
  }, [setBlocks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gestion des changements de blocs
  // En mode onglet-lot, on ne reçoit que le sous-arbre du lot actif :
  // on remplace chaque bloc du sous-arbre à sa position d'origine (pour ne pas
  // réordonner les autres lots), et on insère les blocs nouvellement créés juste
  // après la dernière position connue du sous-arbre.
  const handleBlocksChange = useCallback((newBlocks: Block[]) => {
    if (!activeLotSubtreeIds) {
      setBlocks(newBlocks);
      return;
    }
    setBlocks(prev => {
      const newById = new Map(newBlocks.map(b => [b.id, b]));
      const consumed = new Set<string>();
      const result: Block[] = [];
      for (const b of prev) {
        if (activeLotSubtreeIds.has(b.id)) {
          const replacement = newById.get(b.id);
          if (replacement) {
            result.push(replacement);
            consumed.add(b.id);
          }
          // si absent dans newBlocks → bloc supprimé, on l'omet
        } else {
          result.push(b);
        }
      }
      const additions = newBlocks.filter(b => !consumed.has(b.id));
      if (!additions.length) return result;
      let lastSubtreeIdx = -1;
      for (let i = result.length - 1; i >= 0; i--) {
        if (activeLotSubtreeIds.has(result[i].id)) {
          lastSubtreeIdx = i;
          break;
        }
      }
      if (lastSubtreeIdx < 0) return [...result, ...additions];
      return [
        ...result.slice(0, lastSubtreeIdx + 1),
        ...additions,
        ...result.slice(lastSubtreeIdx + 1),
      ];
    });
  }, [setBlocks, activeLotSubtreeIds]);

  // Ajouter un nouveau lot (niveau 0) et le sélectionner
  const handleAddLot = useCallback(() => {
    const newLot = BlockService.createSection(undefined, 0);
    newLot.content = { ...newLot.content, titre: `LOT ${lots.length + 1}` };
    setBlocks(prev => [...prev, newLot]);
    setActiveLotId(newLot.id);
  }, [lots.length, setBlocks]);

  // Renommer un lot (niveau 0)
  const handleRenameLot = useCallback((id: string, newName: string) => {
    setBlocks(prev =>
      prev.map(b =>
        b.id === id ? { ...b, content: { ...b.content, titre: newName } } : b
      )
    );
  }, [setBlocks]);

  // Supprimer un lot et tout son sous-arbre (prestations + ventilations)
  const handleDeleteLot = useCallback((id: string) => {
    setBlocks(prev => {
      const childrenByParent = new Map<string, string[]>();
      for (const b of prev) {
        if (b.parentId) {
          const arr = childrenByParent.get(b.parentId) ?? [];
          arr.push(b.id);
          childrenByParent.set(b.parentId, arr);
        }
      }
      const toRemove = new Set<string>([id]);
      const frontier: string[] = [id];
      while (frontier.length) {
        const cur = frontier.shift()!;
        const kids = childrenByParent.get(cur);
        if (!kids) continue;
        for (const k of kids) {
          if (!toRemove.has(k)) {
            toRemove.add(k);
            frontier.push(k);
          }
        }
      }
      return prev.filter(b => !toRemove.has(b.id));
    });
  }, [setBlocks]);

  // Mettre à jour le budget initial d'un lot (level 0). undefined = effacer.
  const handleUpdateLotBudgetInitial = useCallback((lotId: string, value: number | undefined) => {
    setBlocks(prev =>
      prev.map(b =>
        b.id === lotId
          ? { ...b, budgetInitial: value }
          : b
      )
    );
  }, [setBlocks]);

  // Pont Ratio → Chiffrage : si l'utilisateur arrive depuis le bouton "Démarrer un chiffrage", on remplace
  // les blocs de la version active par 1 onglet par lot Ratio (budget initial pré-rempli).
  // Si le chiffrage actif a déjà des lots, on demande confirmation avant d'écraser.
  const { pendingPromotion, consumePromotion } = useRatioPromotion();
  useEffect(() => {
    if (!pendingPromotion) return;
    const proceed =
      !hasLots ||
      window.confirm(
        `Le chiffrage actuel contient ${lots.length} lot${lots.length > 1 ? 's' : ''}. ` +
        `Les remplacer par les ${pendingPromotion.lots.length} lot${pendingPromotion.lots.length > 1 ? 's' : ''} venant de Ratio ?`
      );
    if (!proceed) {
      consumePromotion();
      return;
    }
    const newLots: Block[] = pendingPromotion.lots.map((p, i) => {
      const lot = BlockService.createSection(undefined, 0);
      return {
        ...lot,
        order: i,
        content: { ...lot.content, titre: p.nom },
        budgetInitial: p.budgetInitial > 0 ? p.budgetInitial : undefined,
      };
    });
    setBlocks(newLots);
    setActiveLotId(newLots.length === 1 ? newLots[0].id : null);
    consumePromotion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPromotion]);

  // États UI events
  const [showEventMatrix, setShowEventMatrix] = useState(false);

  // Gestion des versions
  const handleSelectVersion = useCallback((id: string) => {
    setTimelineData(prev => ({ ...prev, activeVersionId: id }));
  }, []);

  const handleDuplicateVersion = useCallback(() => {
    setTimelineData(prev => {
      const currentPhase = prev.phases.find(p => p.id === prev.activePhaseId);
      if (!currentPhase) return prev;
      const source = currentPhase.versions.find(v => v.id === prev.activeVersionId);
      if (!source) return prev;
      const versionNumber = currentPhase.versions.length + 1;
      const newVersion = createVersion(`V${versionNumber}`, [...source.blocks]);
      return {
        ...prev,
        phases: prev.phases.map(p =>
          p.id === prev.activePhaseId
            ? {
                ...p,
                versions: [
                  ...p.versions.map(v =>
                    v.status === 'draft' ? { ...v, status: 'draft-old' as const } : v
                  ),
                  newVersion,
                ],
              }
            : p
        ),
        activeVersionId: newVersion.id,
      };
    });
  }, []);

  // Gestion des événements
  const handleAddEvent = useCallback((name: string) => {
    const newEvent: VersionEvent = {
      id: `evt-${Date.now()}`,
      name,
      lotIds: null,
      pointages: {},
    };
    setTimelineData(prev => ({
      ...prev,
      phases: prev.phases.map(p =>
        p.id === prev.activePhaseId
          ? {
              ...p,
              versions: p.versions.map(v =>
                v.id === prev.activeVersionId
                  ? { ...v, events: [...v.events, newEvent] }
                  : v
              ),
            }
          : p
      ),
    }));
  }, []);

  const handlePointEvent = useCallback((eventId: string, lotId: string) => {
    setTimelineData(prev => ({
      ...prev,
      phases: prev.phases.map(p =>
        p.id === prev.activePhaseId
          ? {
              ...p,
              versions: p.versions.map(v =>
                v.id === prev.activeVersionId
                  ? {
                      ...v,
                      events: v.events.map(e =>
                        e.id === eventId
                          ? { ...e, pointages: { ...e.pointages, [lotId]: true } }
                          : e
                      ),
                    }
                  : v
              ),
            }
          : p
      ),
    }));
  }, []);

  const handleOpenCreateEvent = useCallback(() => {
    setShowEventMatrix(true);
  }, []);

  const handleFinalizeVersion = useCallback(() => {
    setTimelineData(prev => ({
      ...prev,
      phases: prev.phases.map(p =>
        p.id === prev.activePhaseId
          ? {
              ...p,
              versions: p.versions.map(v =>
                v.id === prev.activeVersionId
                  ? { ...v, status: 'finalized' as const }
                  : v
              ),
            }
          : p
      ),
    }));
  }, []);

  // Gestion des phases
  const handleSelectPhase = useCallback((phaseId: string) => {
    setTimelineData(prev => {
      const phase = prev.phases.find(p => p.id === phaseId);
      if (!phase || phase.versions.length === 0) return prev;
      const lastVersion = phase.versions[phase.versions.length - 1];
      return { ...prev, activePhaseId: phaseId, activeVersionId: lastVersion.id };
    });
    setShowComparisonPicker(false);
  }, []);

  const handleAdvanceToPhase = useCallback((phaseId: string) => {
    setTimelineData(prev => {
      const currentBlocks = prev.phases
        .find(p => p.id === prev.activePhaseId)
        ?.versions.find(v => v.id === prev.activeVersionId)
        ?.blocks ?? [];
      const newV1 = createVersion('V1', [...currentBlocks]);
      return {
        phases: prev.phases.map(p =>
          p.id === phaseId ? { ...p, versions: [newV1] } : p
        ),
        activePhaseId: phaseId,
        activeVersionId: newV1.id,
      };
    });
    setShowComparisonPicker(false);
  }, []);

  const handleRenamePhase = useCallback((phaseId: string, name: string) => {
    setTimelineData(prev => ({
      ...prev,
      phases: prev.phases.map(p => p.id === phaseId ? { ...p, name } : p),
    }));
  }, []);

  const handleToggleSlot = useCallback((slot: ComparisonSlot) => {
    setComparisonSlots(prev => {
      const key = `${slot.phaseId}/${slot.versionId}`;
      const exists = prev.some(s => `${s.phaseId}/${s.versionId}` === key);
      if (exists) return prev.filter(s => `${s.phaseId}/${s.versionId}` !== key);
      return [...prev, slot];
    });
  }, []);

  // Gestion des changements de sélection
  const handleSelectedBlocksChange = useCallback((newSelectedBlocks: Block[]) => {
    setSelectedBlocks(newSelectedBlocks);
  }, []);

  // Gestion de la désélection de tous les blocs
  const handleClearSelection = useCallback(() => {
    if (clearSelectionRef.current) {
      clearSelectionRef.current();
    }
  }, []);

  // Gestion de l'ajout d'un ensemble — deux modes :
  //  - 'groupe'       : ajoute une sous-section + prestations (dans le lot actif si présent)
  //  - 'ventilation'  : ajoute UNE prestation + ses composants en lignes de coût
  //
  // Si un lot est actif (TOC), le contenu est imbriqué dans ce lot
  // (level = activeLot.level + 1, soit 1 par défaut) ; sinon on ajoute à la racine.
  const handleAddEnsemble = useCallback((ensemble: Ensemble) => {
    setBlocks(prevBlocks => {
      const activeLot = activeLotId ? prevBlocks.find(b => b.id === activeLotId) : undefined;
      const baseLevel = activeLot ? activeLot.level + 1 : 0;
      const parentLotId = activeLot?.id;
      const stamp = Date.now();

      if (ensemble.mode === 'groupe') {
        const newBlocks: Block[] = [];
        ensemble.sections.forEach((section, sectionIndex) => {
          const sectionId = `${ensemble.id}-${section.id}-${stamp}-${sectionIndex}`;
          const prestationBlocks: Block[] = section.blocs.map((bloc, blocIndex) => {
            // Matching DB par fuzzy match sur l'intitulé : les Ensembles ne
            // stockent pas d'id d'ouvrage, on les relie à la bibliothèque pour
            // que clic droit → sources historiques fonctionne.
            const label = bloc.content.intitule || '';
            // Ensemble prestation → F+P (ligne vendue fourni + posé).
            const match = label ? bestMatch(label, ventilationAllData, 'F+P') : null;
            const sources: PrixUnitaireSourceItem[] = match
              ? match.ouvrage.chantiers
                  .map(c => ({
                    id: c.id,
                    nom: c.nom,
                    localisation: c.localisation,
                    annee: c.annee,
                    fournisseur: c.fournisseur,
                    prixUnitaire: parsePrixString(c.prixActualise || c.prixUnitaire),
                    active: true,
                  }))
                  .filter(s => s.prixUnitaire > 0)
              : [];
            const avg = sources.length
              ? sources.reduce((a, s) => a + s.prixUnitaire, 0) / sources.length
              : 0;
            // Si on a une moyenne DB, elle remplace le PU figé dans l'ensemble ;
            // sinon on conserve le prix statique de l'ensemble.
            const prixUnitaire = avg > 0
              ? `${BlockCalculationService.formatPrice(avg)} €`
              : bloc.content.prixUnitaire;
            const qty = BlockCalculationService.parseQuantity(bloc.content.quantite ?? '1');
            const prixTotal = avg > 0
              ? BlockCalculationService.formatPrice(avg * qty)
              : bloc.content.prixTotal;

            return {
              id: `${sectionId}-${bloc.id}-${blocIndex}`,
              parentId: sectionId,
              order: blocIndex,
              level: baseLevel + 1,
              children: [],
              content: {
                titre: bloc.content.intitule || 'Nouvelle prestation',
                intitule: bloc.content.intitule || 'Nouvelle prestation',
                categorie: bloc.content.categorie,
                unite: bloc.content.unite,
                prixUnitaire,
                quantite: bloc.content.quantite,
                prixTotal,
                chantier: bloc.content.chantier,
                fournisseur: bloc.content.fournisseur,
                ...(match && sources.length > 0 ? {
                  matchedItemId: String(match.ouvrage.id),
                  matchedItemType: 'prestation' as const,
                  prixUnitaireSource: 'database' as const,
                  prixUnitaireMatchType: 'prestation' as const,
                  prixUnitaireReliability: sourcesToReliability(sources.length),
                  prixUnitaireSources: sources,
                } : {}),
              },
            };
          });

          const sectionBlock: Block = {
            id: sectionId,
            parentId: parentLotId,
            order: sectionIndex,
            level: baseLevel,
            isCollapsed: false,
            children: prestationBlocks.map(b => b.id),
            content: { titre: section.nom },
          };

          newBlocks.push(sectionBlock, ...prestationBlocks);
        });
        return [...prevBlocks, ...newBlocks];
      }

      // mode 'ventilation' : prestation parente + lignes de coût
      const prestationId = `ventile-${ensemble.id}-${stamp}`;
      const composantBlocks: Block[] = ensemble.composants.map((c, i) => ({
        id: `${prestationId}-comp-${i}`,
        parentId: prestationId,
        order: i,
        level: baseLevel + 1,
        children: [],
        content: {
          ...c.content,
          isVentilationCostLine: true,
        },
      }));
      const prestationBlock: Block = {
        id: prestationId,
        parentId: parentLotId,
        order: 0,
        level: baseLevel,
        children: composantBlocks.map(b => b.id),
        content: {
          ...ensemble.prestation.content,
          matchedItemId: ensemble.id,
          matchedItemType: 'ensemble',
        },
      };
      return [...prevBlocks, prestationBlock, ...composantBlocks];
    });
  }, [setBlocks, activeLotId]);

  // Ensembles créés à la volée par l'utilisateur — mergés avec ensemblesPredefinis
  // dans le dropdown du menu. Non persisté côté backend pour l'instant (vit le
  // temps de la session).
  const [userEnsembles, setUserEnsembles] = useState<Ensemble[]>([]);

  // Sauvegarde d'une sélection comme ensemble réutilisable.
  //
  // Détection automatique du mode :
  //  - 'ventilation' si la sélection = 1 prestation parente + N cost lines
  //    (isVentilationCostLine) dont le parentId pointe vers cette prestation.
  //    → conserver la structure parent/composants pour réinsertion 1:1.
  //  - 'groupe' sinon (comportement historique).
  //
  // On ne filtre pas les cost lines en mode groupe : si l'utilisateur les a
  // sélectionnées explicitement, il est plus prévisible de les conserver.
  const handleSaveSelectionAsEnsemble = useCallback((blocs: Block[], nom: string, description: string) => {
    if (blocs.length < 2 || !nom.trim()) return;
    const now = new Date();
    const id = `user-ensemble-${now.getTime()}`;

    const parents = blocs.filter(b => !b.content.isVentilationCostLine);
    const costLines = blocs.filter(b => b.content.isVentilationCostLine === true);
    const isVentilation =
      parents.length === 1 &&
      costLines.length >= 1 &&
      costLines.every(c => c.parentId === parents[0].id);

    const newEnsemble: Ensemble = isVentilation
      ? {
          id,
          nom: nom.trim(),
          description: description.trim(),
          categorie: 'utilisateur',
          tags: [],
          mode: 'ventilation',
          prestation: { ...parents[0], children: [] },
          composants: costLines.map(c => ({ ...c, children: [] })),
          dateCreation: now,
          dateModification: now,
          auteur: 'Vous',
          version: '1.0',
        }
      : {
          id,
          nom: nom.trim(),
          description: description.trim(),
          categorie: 'utilisateur',
          tags: [],
          mode: 'groupe',
          sections: [{
            id: `${id}-section`,
            nom: nom.trim(),
            description: description.trim(),
            blocs: blocs.map(b => ({ ...b, children: [] })),
          }],
          dateCreation: now,
          dateModification: now,
          auteur: 'Vous',
          version: '1.0',
        };
    setUserEnsembles(prev => [newEnsemble, ...prev]);
  }, []);

  // Gestion de l'export Excel
  const handleExportExcel = useCallback(() => {
    exportChiffrageToCSV(blocks, nomProjet);
  }, [blocks, nomProjet]);

  // Gestion du chargement d'un gabarit
  const handleLoadTemplate = useCallback((template: ProjectTemplate) => {
    setNomProjet(template.nom);
    setInformationsProjet(template.informationsProjet || '');
  }, []);

  // Sauvegarder le chiffrage courant (snapshot avec blocs)
  const handleSaveChiffrage = useCallback((name: string) => {
    const snapshot: SavedChiffrage = {
      id: `saved-${Date.now()}`,
      nom: name || nomProjet,
      savedAt: new Date(),
      blocks: [...blocks],
      budgetTotal,
      surfaceTraitee,
    };
    setSavedChiffrages(prev => [snapshot, ...prev]);
  }, [nomProjet, blocks, budgetTotal, surfaceTraitee]);

  // Charger un chiffrage sauvegardé
  const handleDuplicateFrom = useCallback((chiffrage: SavedChiffrage) => {
    setNomProjet(`${chiffrage.nom} (copie)`);
    setBudgetTotal(chiffrage.budgetTotal);
    setSurfaceTraitee(chiffrage.surfaceTraitee);
    setBlocks(chiffrage.blocks);
  }, [setBlocks]);

  // Gestion de la sauvegarde d'un gabarit
  const handleSaveTemplate = useCallback((name: string) => {
    const newTemplate: ProjectTemplate = {
      id: `template-${Date.now()}`,
      nom: name.trim() || nomProjet,
      description: `Sauvegardé le ${new Date().toLocaleDateString('fr-FR')}`,
      caracteristiques: { typologie: '', annee: new Date().getFullYear().toString(), localisation: '', projet: nomProjet, fournisseur: '', categorie: '', prixMin: '', prixMax: '' },
      informationsProjet,
    };
    setTemplates(prev => [...prev, newTemplate]);
  }, [nomProjet, informationsProjet]);

  // Gestion de la sélection d'un résultat de recherche
  //
  // Un "usage défini" (prestation seule) doit s'ajouter dans le lot actif :
  // on pose parentId/level depuis le lot actif quand il existe.
  // Les lots restent toujours à la racine (un lot ne s'imbrique pas dans un lot).
  // Les ensembles sont délégués à handleAddEnsemble qui gère déjà l'activeLotId.
  const handleSearchResultSelect = useCallback((result: any) => {
    if (result.type === 'ensemble') {
      handleAddEnsemble(result.data);
      return;
    }

    setBlocks(prevBlocks => {
      const blockId = `block-${Date.now()}`;

      if (result.type === 'lot') {
        // Les lots (level 0) restent à la racine.
        const newBlock: Block = {
          id: blockId,
          order: prevBlocks.length,
          level: 0,
          children: [],
          content: {
            titre: result.nom,
            prixTotal: '0',
          },
        };
        return [...prevBlocks, newBlock];
      }

      // Prestation seule : cible le lot actif s'il existe.
      const activeLot = activeLotId ? prevBlocks.find(b => b.id === activeLotId) : undefined;
      const parentId = activeLot?.id;
      const level = activeLot ? activeLot.level + 1 : 0;
      const siblingsCount = activeLot
        ? prevBlocks.filter(b => b.parentId === activeLot.id).length
        : prevBlocks.filter(b => !b.parentId).length;

      // Matching DB : construit les sources historiques depuis les chantiers
      // de l'Ouvrage, pour que clic droit → PuSourcesModal fonctionne et que
      // le PU reflète la moyenne (non le premier chantier seul).
      const ouvrage = result.data;
      const sources: PrixUnitaireSourceItem[] = (ouvrage.chantiers ?? [])
        .map((c: any) => ({
          id: c.id,
          nom: c.nom,
          localisation: c.localisation,
          annee: c.annee,
          fournisseur: c.fournisseur,
          prixUnitaire: parsePrixString(c.prixActualise || c.prixUnitaire),
          active: true,
        }))
        .filter((s: PrixUnitaireSourceItem) => s.prixUnitaire > 0);
      const avg = sources.length
        ? sources.reduce((a, s) => a + s.prixUnitaire, 0) / sources.length
        : 0;

      // ─── Branche F (fourniture nue) ───────────────────────────────────────
      // Un item F ajouté depuis la recherche n'est pas un PU F+P direct mais
      // un coût d'achat matière. On crée une ligne pré-décomposée en
      // **fourniture-seule** systématiquement — MO vide. Le label FPLabel sur
      // la ligne sort donc « F » (mode === 'fourniture-seule'), conformément
      // à la nature de l'ouvrage sélectionné.
      //
      // On ne pré-remplit PAS la MO depuis tempsPoseRef/nbHommesRef : sélectionner
      // un F en biblio signifie qu'on veut une ligne de fourniture, pas une F+P
      // déguisée. Si l'utilisateur veut ajouter de la MO ensuite, le toggle
      // « + MO » dans la subbar fera le travail (et la sélection de fourniture
      // dans la subbar peut, elle, pré-remplir depuis les refs).
      if (ouvrage.type === 'F') {
        const coutF = typeof ouvrage.coutFournitureRef === 'number'
          ? ouvrage.coutFournitureRef
          : avg;
        const baseContent: BlockContent = {
          titre: result.nom,
          intitule: result.nom,
          categorie: result.categorie,
          unite: result.unite,
          quantite: '1',
          decompositionMode: 'fourniture-seule' as DecompositionMode,
          decompositionSource: 'database',
          fournitureIntitule: ouvrage.intitule,
          coutFourniture: coutF > 0 ? +coutF.toFixed(2) : undefined,
          fournitureSource: 'database',
          fournitureReliability: sourcesToReliability(sources.length),
          fournitureSources: sources,
          ...(sources.length > 0 ? {
            matchedItemId: String(ouvrage.id),
            matchedItemType: 'prestation' as const,
          } : {}),
        };
        // Propage PU/total calculés depuis la décomposition (F + MO éventuelle).
        const content = DecompositionCalculationService.applyBreakdownToContent(
          baseContent,
          '1',
          decompositionParams,
        );
        const newBlock: Block = {
          id: blockId,
          parentId,
          order: siblingsCount,
          level,
          children: [],
          content,
        };
        return [...prevBlocks, newBlock];
      }

      // ─── Branche F+P (cas historique) ─────────────────────────────────────
      const prixUnitaire = avg > 0
        ? `${BlockCalculationService.formatPrice(avg)} €`
        : (ouvrage.chantiers?.[0]?.prixUnitaire
          ? `${BlockCalculationService.formatPrice(parsePrixString(ouvrage.chantiers[0].prixUnitaire))} €`
          : '0 €');

      const newBlock: Block = {
        id: blockId,
        parentId,
        order: siblingsCount,
        level,
        children: [],
        content: {
          titre: result.nom,
          intitule: result.nom,
          categorie: result.categorie,
          unite: result.unite,
          quantite: '1',
          prixUnitaire,
          ...(sources.length > 0 ? {
            matchedItemId: String(ouvrage.id),
            matchedItemType: 'prestation' as const,
            prixUnitaireSource: 'database' as const,
            prixUnitaireMatchType: 'prestation' as const,
            prixUnitaireReliability: sourcesToReliability(sources.length),
            prixUnitaireSources: sources,
          } : {}),
        },
      };
      return [...prevBlocks, newBlock];
    });
  }, [handleAddEnsemble, setBlocks, activeLotId, decompositionParams]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1440px] mx-auto px-4 py-8">
        {/* Header */}
        <ChiffHeader
          onImport={handleImport}
          isImporting={importPhase !== 'idle'}
        />

        {/* Project Management — pleine largeur */}
        <section className="p-4 bg-white shadow rounded-xl mb-6">
          <ProjectHeader
            nomProjet={nomProjet}
            onNomProjetChange={setNomProjet}
            onExportExcel={handleExportExcel}
            onLoadTemplate={handleLoadTemplate}
            onSaveTemplate={handleSaveTemplate}
            templates={templates}
            savedChiffrages={savedChiffrages}
            onDuplicateFrom={handleDuplicateFrom}
            onSaveChiffrage={handleSaveChiffrage}
            projectParameters={projectParameters}
            onProjectParametersChange={setProjectParameters}
            visibleParams={visibleParams}
            onVisibleParamsChange={setVisibleParams}
            activeParams={activeParams}
            onActiveParamsChange={setActiveParams}
          />
          <ProjectSummary
            budgetTotal={budgetTotal}
            surfaceTraitee={surfaceTraitee}
            informationsProjet={informationsProjet}
            onBudgetChange={setBudgetTotal}
            onSurfaceChange={setSurfaceTraitee}
            onInformationsChange={setInformationsProjet}
          />
        </section>

        {/* Phases + versions + bouton comparer sur une seule ligne */}
        <div className="flex items-center justify-between mb-1 min-w-0 gap-4">
          <PhaseBar
            phases={timelineData.phases}
            activePhaseId={timelineData.activePhaseId}
            activeVersionName={versions.find(v => v.id === activeVersionId)?.name ?? ''}
            onSelectPhase={handleSelectPhase}
            onAdvanceToPhase={handleAdvanceToPhase}
            onRenamePhase={handleRenamePhase}
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <VersionTabs
              versions={versions}
              activeVersionId={activeVersionId}
              onSelectVersion={handleSelectVersion}
              onDuplicateVersion={handleDuplicateVersion}
            />
            <div className="w-px h-3.5 bg-gray-200" />
            <button
              type="button"
              onClick={() => { setShowComparisonPicker(v => !v); if (showComparisonPicker) setComparisonSlots([]); }}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                showComparisonPicker || isComparisonMode
                  ? 'bg-violet-600 text-white hover:bg-violet-700'
                  : 'text-gray-400 hover:text-violet-600 hover:bg-violet-50'
              }`}
              title="Comparer des versions entre phases"
            >
              <ArrowsRightLeftIcon className="w-3 h-3" />
              <span>Comparer</span>
              {isComparisonMode && <span className="ml-0.5 opacity-80">({comparisonSlots.length})</span>}
            </button>
            <div className="w-px h-3.5 bg-gray-200" />
            <button
              type="button"
              onClick={() => setShowEventMatrix(v => !v)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                showEventMatrix
                  ? 'bg-violet-600 text-white hover:bg-violet-700'
                  : 'text-gray-400 hover:text-violet-600 hover:bg-violet-50'
              }`}
              title="Matrice de pointage"
            >
              <TableCellsIcon className="w-3 h-3" />
              <span>Pointages</span>
              {pendingPointagesCount > 0 && (
                <span className={`ml-0.5 px-1 py-px rounded text-[10px] font-bold leading-none ${showEventMatrix ? 'bg-white/20' : 'bg-orange-100 text-orange-600'}`}>
                  {pendingPointagesCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Sélecteur de comparaison inter-phases */}
        {showComparisonPicker && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Sélectionner les versions à comparer</span>
              <div className="flex items-center gap-2">
                {comparisonSlots.length >= 2 && (
                  <button
                    onClick={() => { setShowComparisonPicker(false); }}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                  >
                    <ArrowsRightLeftIcon className="w-3 h-3" />
                    Comparer ({comparisonSlots.length})
                  </button>
                )}
                <button
                  onClick={() => { setShowComparisonPicker(false); setComparisonSlots([]); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Fermer"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              {timelineData.phases.map(phase => {
                if (phase.versions.length === 0) return (
                  <div key={phase.id} className="flex items-center gap-2">
                    <span className="text-xs text-gray-300 w-32 flex-shrink-0 truncate">{phase.name}</span>
                    <span className="text-xs text-gray-300 italic">— pas encore démarrée</span>
                  </div>
                );
                return (
                  <div key={phase.id} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-32 flex-shrink-0 truncate">{phase.name}</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      {phase.versions.map(v => {
                        const key = `${phase.id}/${v.id}`;
                        const isSelected = comparisonSlots.some(s => `${s.phaseId}/${s.versionId}` === key);
                        return (
                          <button
                            key={v.id}
                            onClick={() => handleToggleSlot({ phaseId: phase.id, phaseName: phase.name, versionId: v.id, versionName: v.name, blocks: v.blocks })}
                            className={`px-2 py-0.5 rounded text-xs font-medium transition-all border ${
                              isSelected
                                ? 'bg-violet-600 text-white border-violet-600'
                                : 'bg-white border-gray-300 text-gray-600 hover:border-violet-400 hover:text-violet-600'
                            }`}
                          >
                            {v.name}
                            {isSelected && comparisonSlots[0] && `${comparisonSlots[0].phaseId}/${comparisonSlots[0].versionId}` === key && (
                              <span className="ml-1 text-[10px] opacity-75">réf</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {comparisonSlots.length === 1 && (
              <p className="text-xs text-gray-400 mt-2 italic">Sélectionnez au moins une autre version pour comparer</p>
            )}
          </div>
        )}

        {/* Bandeau brouillon ancienne */}
        {activeVersion?.status === 'draft-old' && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-lg mb-2 text-sm text-orange-700">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 text-orange-500" />
            <span className="font-medium">Version brouillon ancienne</span>
            <span className="text-orange-300">·</span>
            <span className="text-orange-600">
              {pendingPointagesCount > 0
                ? `${pendingPointagesCount} pointage${pendingPointagesCount !== 1 ? 's' : ''} en attente`
                : 'Tous les pointages sont à jour'}
            </span>
            <button
              type="button"
              onClick={pendingPointagesCount === 0 ? handleFinalizeVersion : () => setShowEventMatrix(true)}
              className={`ml-auto px-3 py-1 rounded text-xs font-medium transition-colors ${
                pendingPointagesCount === 0
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer'
                  : 'bg-orange-100 text-orange-500 hover:bg-orange-200 cursor-pointer'
              }`}
              title={pendingPointagesCount > 0 ? 'Voir les pointages en attente' : 'Finaliser cette version'}
            >
              {pendingPointagesCount > 0 ? 'Voir les pointages' : 'Finaliser'}
            </button>
          </div>
        )}

        {/* Matrice de pointage */}
        {showEventMatrix && (
          <VersionEventMatrix
            versionName={activeVersion?.name ?? ''}
            events={activeEvents}
            lots={lots}
            pendingCount={pendingPointagesCount}
            onClose={() => setShowEventMatrix(false)}
            onPointEvent={handlePointEvent}
            onAddEvent={handleAddEvent}
            onFinalizeVersion={handleFinalizeVersion}
          />
        )}

        {/* Consultations existantes */}
        {consultations.length > 0 && (
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Consultations</span>
            {consultations.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveConsultationId(c.id)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                {c.name}
                <span className="text-orange-400">({c.companies.length} ent.)</span>
              </button>
            ))}
          </div>
        )}

        {/* Menu de chiffrage + bouton Vente */}
        <div className="mb-2 flex items-center gap-2">
          <div className="flex-1 min-w-0">
          <ChiffMenu
            onSearch={() => {}}
            onAddEnsemble={handleAddEnsemble}
            onSaveSelectionAsEnsemble={handleSaveSelectionAsEnsemble}
            onSearchResultSelect={handleSearchResultSelect}
            selectedBlocks={selectedBlocks}
            allBlocks={blocks}
            userEnsembles={userEnsembles}
            onModalOpenChange={setIsModalOpen}
            onClearSelection={handleClearSelection}
            onConsulter={handleConsulter}
          />
          </div>
          <button
            onClick={isComparisonMode ? undefined : () => setShowFeuilleDeVente(v => !v)}
            disabled={isComparisonMode}
            title={isComparisonMode ? 'Non disponible en mode comparaison' : undefined}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg transition-all flex-shrink-0 ${
              isComparisonMode
                ? 'border border-gray-200 text-gray-300 cursor-not-allowed'
                : showFeuilleDeVente
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200 active:scale-95'
                : 'border border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400 active:scale-95'
            }`}
          >
            <PresentationChartBarIcon className="w-4 h-4" />
            <span>Vente</span>
          </button>
        </div>

        {/* Tableau de comparaison — pleine largeur, remplace l'éditeur */}
        {isComparisonMode && (
          <div className="mb-6">
            <VersionComparisonPanel
              slots={comparisonSlots}
              onClose={() => { setComparisonSlots([]); setShowComparisonPicker(false); }}
            />
          </div>
        )}

        {/* Onglets lots : (+) + Récap (si >1 lot) + un onglet par lot */}
        {!isComparisonMode && importPhase === 'idle' && !activeConsultationId && (
          <div className="mb-2">
            <LotTabs
              lots={lots}
              activeLotId={activeLotId}
              onSelectLot={setActiveLotId}
              showRecap={showRecapTab}
              onAddLot={handleAddLot}
              onRenameLot={handleRenameLot}
              onDeleteLot={handleDeleteLot}
              lotBudgets={lotBudgets}
              onUpdateBudgetInitial={handleUpdateLotBudgetInitial}
            />
          </div>
        )}

        {/* Zone éditeur + panneau vente côte à côte (masqué en mode comparaison) */}
        {!isComparisonMode && (
        <div className={showFeuilleDeVente ? 'flex gap-2 items-start' : undefined}>

          {/* ── Colonne centre : éditeur / ventilation / loading ── */}
          <div className={showFeuilleDeVente ? 'flex-1 min-w-0' : undefined}>

            {/* Phase loader */}
            {importPhase === 'loading' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 -mt-2 min-h-[440px] flex flex-col items-center justify-center gap-8">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 rounded-full border-4 border-violet-100" />
                  <div className="absolute inset-0 rounded-full border-4 border-violet-600 border-t-transparent animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ArrowUpTrayIcon className="w-5 h-5 text-violet-600" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-800">Analyse du devis en cours…</p>
                  <p className="text-sm text-gray-500 mt-1">Extraction automatique des prestations</p>
                </div>
                <div className="flex flex-col gap-3 w-72">
                  {LOADING_STEPS.map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      {i < loadingStep ? (
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : i === loadingStep ? (
                        <div className="w-5 h-5 rounded-full border-2 border-violet-500 border-t-transparent animate-spin flex-shrink-0" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-100 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${
                        i < loadingStep ? 'text-gray-700 font-medium'
                        : i === loadingStep ? 'text-violet-700 font-medium'
                        : 'text-gray-400'
                      }`}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phase animation des prix */}
            {importPhase === 'animating' && (
              <ImportAnimationOverlay
                targetBlocks={getDefaultBlocksArray()}
                reliabilityMap={reliabilityMap}
                onComplete={handleImportComplete}
              />
            )}

            {/* Page consultation — remplace l'éditeur quand une consultation est ouverte */}
            {importPhase === 'idle' && activeConsultationId && (() => {
              const consultation = consultations.find(c => c.id === activeConsultationId);
              if (!consultation) return null;
              return (
                <ConsultationPage
                  consultation={consultation}
                  onBack={() => setActiveConsultationId(null)}
                  onUpdate={handleUpdateConsultation}
                  onRetain={(companyId) => handleRetainCompany(activeConsultationId!, companyId)}
                />
              );
            })()}

            {/* Affichage normal — Récap (résumé par lot) ou éditeur du lot actif */}
            {importPhase === 'idle' && !activeConsultationId && activeLotId === null && hasLots && showRecapTab && (
              <RecapSummary blocks={blocks} lots={lots} lotBudgets={lotBudgets} onSelectLot={setActiveLotId} />
            )}
            {importPhase === 'idle' && !activeConsultationId && (activeLotId !== null || !showRecapTab) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 -mt-2">
                <BlockEditor
                  key={`${importKey}-${activeVersionId}-${activeLotId ?? 'recap'}`}
                  initialBlocks={visibleBlocks}
                  onBlocksChange={handleBlocksChange}
                  onSelectedBlocksChange={handleSelectedBlocksChange}
                  onSelectAll={selectAllRef}
                  onClearSelection={clearSelectionRef}
                  isModalOpen={isModalOpen}
                  className="min-h-[400px]"
                  consultationMap={consultationMap}
                  onOpenConsultation={handleOpenConsultation}
                  events={activeEvents}
                  onPointEvent={handlePointEvent}
                  onOpenCreateEvent={handleOpenCreateEvent}
                  chiffrageView={chiffrageView}
                  onChiffrageViewChange={setChiffrageView}
                  decompositionParams={decompositionParams}
                  activeLotId={activeLotId ?? undefined}
                />
              </div>
            )}
          </div>

          {/* ── Colonne droite : feuille de vente (optionnelle) ── */}
          {showFeuilleDeVente && (
            <div className="w-72 flex-shrink-0">
              <FeuilleDeVentePanel
                blocks={blocks}
                surfaceTraitee={surfaceTraitee}
                budgetTotal={budgetTotal}
                state={feuilleDeVente}
                onChange={setFeuilleDeVente}
              />
            </div>
          )}
        </div>
        )}
      </div>

    </div>
  );
};

export default ChiffrageContainer;
